import React, { useState, useEffect } from 'react';
import './AdminPanel.css';
import Cookies from 'js-cookie';

function AdminPanel({ socket, userId, isAdmin, adminPassword, tradingEnabled, products }) {
  const [messageForm, setMessageForm] = useState({
    targetUserId: '',
    message: ''
  });
  const [adminResponses, setAdminResponses] = useState([]);
  const [productVisibility, setProductVisibility] = useState({});
  const [fileUpload, setFileUpload] = useState(null);
  const [processingStatus, setProcessingStatus] = useState('');

  // Listen for product visibility messages from the server
  useEffect(() => {
    if (!socket) return;

    const handleMessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === 'productVisibility') {
        setProductVisibility(prev => ({
          ...prev,
          [data.product]: data.visible
        }));
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket]);

  // Initialize product visibility state when products prop changes
  useEffect(() => {
    if (products && products.length > 0) {
      const initialVisibility = {};
      products.forEach(product => {
        // Keep existing visibility state if it exists, otherwise default to true
        initialVisibility[product] = productVisibility[product] !== undefined ? productVisibility[product] : true;
      });
      setProductVisibility(initialVisibility);
    }
  }, [products]);

  const getAdminPassword = () => {
    const credentials = JSON.parse(Cookies.get('credentials') || '{}');
    return credentials.isAdmin ? credentials.password : null;
  };

  const handleTradingToggle = () => {
    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      // console.log('Cannot toggle trading: missing socket or admin credentials');
      return;
    }

    const command = {
      type: 'adminCommand',
      command: 'setTradingEnabled',
      enabled: !tradingEnabled,
      adminPassword: adminPwd
    };
    // console.log('Sending trading toggle command:', command);
    socket.send(JSON.stringify(command));
  };

  const handleClearOrders = () => {
    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      return;
    }

    if (window.confirm('Are you sure you want to clear all orders? This action cannot be undone.')) {
      const command = {
        type: 'adminCommand',
        command: 'clearOrders',
        adminPassword: adminPwd
      };
      socket.send(JSON.stringify(command));
    }
  };

  const handleSettleExchange = () => {
    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      return;
    }

    if (window.confirm('Are you sure you want to settle the exchange? This will calculate final profits and generate a leaderboard. This action cannot be undone.')) {
      const command = {
        type: 'adminCommand',
        command: 'settleExchange',
        adminPassword: adminPwd
      };
      socket.send(JSON.stringify(command));
    }
  };

  const handleMessageSubmit = (e) => {
    e.preventDefault();
    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      // console.log('Cannot send message: missing socket or admin credentials');
      return;
    }

    const command = {
      type: 'adminCommand',
      command: 'sendMessage',
      targetUserId: messageForm.targetUserId,
      message: messageForm.message,
      adminPassword: adminPwd
    };
    console.log('Sending admin message command:', command);
    socket.send(JSON.stringify(command));

    // Clear form
    setMessageForm({
      targetUserId: '',
      message: ''
    });
  };

  const handleProductVisibilityToggle = (product) => {
    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      return;
    }

    // Update local state
    const newVisibility = {
      ...productVisibility,
      [product]: !productVisibility[product]
    };
    setProductVisibility(newVisibility);

    // Send command to server
    const command = {
      type: 'adminCommand',
      command: 'setProductVisibility',
      product: product,
      visible: newVisibility[product],
      adminPassword: adminPwd
    };
    socket.send(JSON.stringify(command));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessageForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFileUpload(e.target.files[0]);
    setProcessingStatus('');
  };

  const handleBulkMessageUpload = async (e) => {
    e.preventDefault();
    if (!fileUpload) {
      setProcessingStatus('No file selected');
      return;
    }

    const adminPwd = getAdminPassword();
    if (!socket || !adminPwd) {
      setProcessingStatus('Admin credentials not available');
      return;
    }

    try {
      const text = await fileUpload.text();
      const lines = text.split('\n').filter(line => line.trim());
      const totalLines = lines.length;
      
      setProcessingStatus(`Processing ${totalLines} messages...`);
      
      let processed = 0;
      let succeeded = 0;
      
      for (const line of lines) {
        const spaceIndex = line.indexOf(' ');
        if (spaceIndex === -1) continue;
        
        const teamId = line.substring(0, spaceIndex);
        const message = line.substring(spaceIndex + 1);
        
        if (teamId && message) {
          const command = {
            type: 'adminCommand',
            command: 'sendMessage',
            targetUserId: teamId,
            message: message,
            adminPassword: adminPwd
          };
          
          try {
            socket.send(JSON.stringify(command));
            succeeded++;
          } catch (err) {
            console.error(`Error sending message to ${teamId}:`, err);
          }
        }
        
        processed++;
        if (processed % 5 === 0 || processed === totalLines) {
          setProcessingStatus(`Processed ${processed}/${totalLines} messages (${succeeded} sent)`);
          // Small delay to allow UI to update
          await new Promise(resolve => setTimeout(resolve, 10));
        }
      }
      
      setProcessingStatus(`Completed: ${succeeded}/${totalLines} messages sent successfully`);
      setFileUpload(null);
      
      // Reset file input
      const fileInput = document.getElementById('bulk-message-file');
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('Error processing file:', error);
      setProcessingStatus(`Error: ${error.message}`);
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="admin-panel">
      <h2>Admin Panel</h2>
      
      <div className="admin-section">
        <h3>Trading Control</h3>
        <div className="trading-control">
          <span>Trading is currently {tradingEnabled ? 'enabled' : 'disabled'}</span>
          <button 
            onClick={handleTradingToggle}
            className={`toggle-button ${tradingEnabled ? 'enabled' : 'disabled'}`}
          >
            {tradingEnabled ? 'Disable Trading' : 'Enable Trading'}
          </button>
        </div>
        <div className="trading-control">
          <button 
            onClick={handleClearOrders}
            className="clear-orders-button"
          >
            Clear All Orders
          </button>
          <button 
            onClick={handleSettleExchange}
            className="settle-exchange-button"
          >
            Settle Exchange
          </button>
        </div>
      </div>

      {products && products.length > 0 && (
        <div className="admin-section">
          <h3>Product Display Controls</h3>
          <div className="product-visibility-controls">
            {products.map(product => (
              <div key={product} className="product-visibility-item">
                <label className="product-visibility-label">
                  <input
                    type="checkbox"
                    checked={productVisibility[product] || false}
                    onChange={() => handleProductVisibilityToggle(product)}
                  />
                  <span>{product}</span>
                </label>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="admin-section">
        <h3>Send Admin Message</h3>
        <form onSubmit={handleMessageSubmit} className="message-form">
          <div className="form-group">
            <label>Target User ID:</label>
            <input
              type="text"
              name="targetUserId"
              value={messageForm.targetUserId}
              onChange={handleInputChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Message:</label>
            <textarea
              name="message"
              value={messageForm.message}
              onChange={handleInputChange}
              required
            />
          </div>
          <button type="submit">Send Message</button>
        </form>
      </div>

      <div className="admin-section">
        <h3>Bulk Message Upload</h3>
        <form onSubmit={handleBulkMessageUpload} className="message-form">
          <div className="form-group">
            <label>Upload Text File (format: "team message"):</label>
            <input
              type="file"
              id="bulk-message-file"
              accept=".txt"
              onChange={handleFileChange}
              className="file-upload"
            />
          </div>
          {processingStatus && (
            <div className="processing-status">
              {processingStatus}
            </div>
          )}
          <button type="submit" disabled={!fileUpload}>
            Process and Send Messages
          </button>
        </form>
      </div>

      {adminResponses.length > 0 && (
        <div className="admin-section">
          <h3>Admin Responses</h3>
          <div className="admin-responses">
            {adminResponses.map((response, index) => (
              <div key={index} className="admin-response">
                {response}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel; 